import { FC, useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Button,
  Progress,
  Spinner,
} from '@chakra-ui/react';
import {
  MdPlayCircle,
  MdDescription,
  MdQuiz,
  MdCheckCircle,
  MdRadioButtonUnchecked
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { Course, Content, getCourseEnrollmentId, getCourseCompletionsByCourseId } from '../../services/university-service';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@bemap/utils';

interface ContentSidebarProps {
  course: Course;
}

interface CompletionData {
  contentCompletions: Array<{
    contentId: number;
    isCompleted: boolean;
    score?: number;
    timeSpent: number;
  }>;
  moduleCompletions: Array<{
    moduleId: number;
    isCompleted: boolean;
    progressPercentage: number;
    completedContents: number;
    totalContents: number;
  }>;
}

const ContentSidebar: FC<ContentSidebarProps> = ({
  course,
}) => {
  const [completionData, setCompletionData] = useState<CompletionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastViewedContentId, setLastViewedContentId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { courseId, contentId } = useParams<{ courseId: string; contentId?: string }>();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const { t } = useTranslation();
  const { user } = useAuth();

  useEffect(() => {
    if (course && user) {
      loadCompletionData();
    }
  }, [course.id, user?.id]);

  const loadCompletionData = async () => {
    if (!course || !user) return;

    try {
      setLoading(true);

      // Get course enrollment ID
      const enrollmentResponse = await getCourseEnrollmentId(Number(courseId), user.id);
      if (enrollmentResponse.statusCode === 200) {
        setLastViewedContentId(enrollmentResponse.message.lastViewedContentId);

        // Get completion data
        const completionResponse = await getCourseCompletionsByCourseId(Number(courseId));
        if (completionResponse.statusCode === 200) {
          setCompletionData(completionResponse.message);
        } else {
          console.warn('Completion data response not successful:', completionResponse);
        }
      } else {
        console.warn('Enrollment response not successful:', enrollmentResponse);
      }
    } catch (error) {
      console.error('Error loading completion data:', error);
      // Set empty completion data to prevent further errors
      setCompletionData({
        contentCompletions: [],
        moduleCompletions: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContentClick = async (content: Content) => {
    if (!user || !courseId) return;

    try {
      // Navigate to content using route parameters
      navigate(`/university/courses/${courseId}/content/${content.id}`);
      //await updateLastViewedContent(course.id, user.id, content.id);
      setLastViewedContentId(content.id);
    } catch (error) {
      console.error('Error updating last viewed content:', error);
    }
  };

  const getContentCompletionStatus = (contentId: number) => {
    if (!completionData || !completionData.contentCompletions) return null;
    return completionData.contentCompletions.find(c => c.contentId === contentId);
  };

  const getModuleCompletionStatus = (moduleId: number) => {
    if (!completionData || !completionData.moduleCompletions) return null;
    return completionData.moduleCompletions.find(m => m.moduleId === moduleId);
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video':
        return MdPlayCircle;
      case 'document':
        return MdDescription;
      case 'quiz':
        return MdQuiz;
      default:
        return MdDescription;
    }
  };

  const getContentColor = (type: string) => {
    switch (type) {
      case 'video':
        return 'red.500';
      case 'document':
        return 'blue.500';
      case 'quiz':
        return 'green.500';
      default:
        return 'gray.500';
    }
  };

  const formatDuration = (duration: string) => {
    return duration;
  };

  const calculateOverallProgress = () => {
    if (!completionData || !completionData.contentCompletions) return course.progress || 0;

    const totalContents = completionData.contentCompletions.length;
    const completedContents = completionData.contentCompletions.filter(c => c.isCompleted).length;

    return totalContents > 0 ? Math.round((completedContents / totalContents) * 100) : 0;
  };

  // Auto-navigate to last viewed content if no content is selected
  useEffect(() => {
    if (!contentId && lastViewedContentId && course.modules && courseId) {
      // Find the content in the course modules
      let foundContent: Content | null = null;
      for (const module of course.modules) {
        if (module.contents) {
          foundContent = module.contents.find(c => c.id === lastViewedContentId) || null;
          if (foundContent) break;
        }
      }

      if (foundContent) {
        handleContentClick(foundContent);
      }
    }
  }, [lastViewedContentId, contentId, course.modules, courseId]);

  return (
    <Box
      bg={bgColor}
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
      maxH="calc(100vh - 120px)"
      overflowY="auto"
    >
      <VStack align="stretch" spacing={4}>
        {/* Course Info */}
        <Box>
          <Text fontSize="lg" fontWeight="bold" color={textColor} mb={2}>
            {course.title}
          </Text>
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.500">
                {t('university.contentSidebar.overall_progress')}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {loading ? (
                  <Spinner size="xs" />
                ) : (
                  `${calculateOverallProgress()}%`
                )}
              </Text>
            </HStack>
            <Progress
              value={loading ? 0 : calculateOverallProgress()}
              colorScheme="blue"
              size="sm"
              borderRadius="full"
            />
          </VStack>
        </Box>

        <Box borderTop="1px" borderColor={borderColor} pt={4}>
          <Text fontSize="md" fontWeight="semibold" color={textColor} mb={3}>
            {t('university.contentSidebar.course_content')}
          </Text>
        </Box>

        {/* Modules and Content */}
        <Accordion allowMultiple defaultIndex={[0]}>
          {course.modules?.map((module) => {
            const moduleCompletion = getModuleCompletionStatus(module.id);
            const moduleProgress = moduleCompletion?.progressPercentage || module.progress || 0;
            const completedContents = moduleCompletion?.completedContents || 0;
            const totalContents = moduleCompletion?.totalContents || module.contents?.length || 0;

            return (
              <AccordionItem key={module.id} border="none">
                <AccordionButton
                  px={0}
                  py={2}
                  _hover={{ bg: 'transparent' }}
                  _expanded={{ bg: 'transparent' }}
                >
                  <Box flex="1" textAlign="left">
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="medium" color={textColor}>
                          {module.title}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {t('university.contentSidebar.module_info', {
                            items: totalContents,
                            duration: module.durationMinutes
                          })}
                        </Text>
                        {moduleCompletion && (
                          <Text fontSize="xs" color="green.500">
                            {t('university.contentSidebar.completed_contents', {
                              completed: completedContents,
                              total: totalContents
                            })}
                          </Text>
                        )}
                      </VStack>
                      <HStack spacing={2}>
                        <Progress
                          value={moduleProgress}
                          colorScheme="green"
                          size="xs"
                          width="60px"
                          borderRadius="full"
                        />
                        <AccordionIcon />
                      </HStack>
                    </HStack>
                  </Box>
                </AccordionButton>
                <AccordionPanel px={0} pb={2}>
                  <VStack align="stretch" spacing={1}>
                    {module.contents?.map((content) => {
                      const contentCompletion = getContentCompletionStatus(content.id);
                      const isCompleted = contentCompletion?.isCompleted || content.completed;
                      const isSelected = contentId === content.id.toString();
                      const isLastViewed = lastViewedContentId === content.id;

                      return (
                        <Button
                          key={content.id}
                          variant="ghost"
                          size="sm"
                          justifyContent="flex-start"
                          px={3}
                          py={2}
                          h="auto"
                          minH="auto"
                          bg={isSelected ? 'blue.50' : isLastViewed && !isSelected ? 'yellow.50' : 'transparent'}
                          color={isSelected ? 'blue.600' : isLastViewed && !isSelected ? 'yellow.700' : textColor}
                          borderLeft={isSelected ? '3px solid' : isLastViewed && !isSelected ? '3px solid' : 'none'}
                          borderColor={isSelected ? 'blue.500' : 'yellow.400'}
                          _hover={{ bg: hoverBg }}
                          onClick={() => handleContentClick(content)}
                        >
                          <HStack spacing={3} align="start" w="100%">
                            <Icon
                              as={getContentIcon(content.type)}
                              color={getContentColor(content.type)}
                              boxSize={4}
                              mt={0.5}
                            />
                            <VStack align="start" spacing={0} flex={1}>
                              <HStack justify="space-between" w="100%">
                                <Box>
                                  <Text fontSize="sm" fontWeight="medium" textAlign="left" display="inline">
                                    {content.title}
                                  </Text>
                                  {isLastViewed && !isSelected && (
                                    <Text fontSize="xs" color="yellow.600" ml={2} display="inline">
                                      ({t('university.contentSidebar.last_viewed')})
                                    </Text>
                                  )}
                                </Box>
                                <HStack spacing={1}>
                                  {isCompleted ? (
                                    <Icon as={MdCheckCircle} color="green.500" boxSize={4} />
                                  ) : (
                                    <Icon as={MdRadioButtonUnchecked} color="gray.400" boxSize={4} />
                                  )}
                                </HStack>
                              </HStack>
                              <Box>
                                <Text fontSize="xs" color="gray.500" display="inline">
                                  {formatDuration(content.duration)}
                                </Text>
                                {contentCompletion?.timeSpent && contentCompletion.timeSpent > 0 ? (
                                  <>
                                    <Text fontSize="xs" color="gray.500" display="inline"> • </Text>
                                    <Text fontSize="xs" color="gray.500" display="inline">
                                      {t('university.contentSidebar.time_spent', { seconds: contentCompletion.timeSpent })}
                                    </Text>
                                  </>
                                ) : null}
                                {content.type === 'quiz' && contentCompletion?.score && (
                                  <>
                                    <Text fontSize="xs" color="gray.500" display="inline"> • </Text>
                                    <Text fontSize="xs" color="gray.500" display="inline">
                                      {t('university.contentSidebar.quiz_score', { score: parseFloat(contentCompletion.score.toString()).toFixed(1) })}
                                    </Text>
                                  </>
                                )}
                              </Box>
                            </VStack>
                          </HStack>
                        </Button>
                      );
                    })}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </VStack>
    </Box>
  );
};

export default ContentSidebar; 